import { useState, useEffect } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'
import { useOrgPermissions } from '@/hooks/useOrgPermissions'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { useToast } from '@/hooks/use-toast'
import { Upload, Download, CheckCircle, AlertCircle, ShieldCheck, Loader2, ToggleLeft, Share2, BarChart3, Newspaper, KeyRound } from 'lucide-react'
import { Switch } from '@/components/ui/switch'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { OrgCharterView } from '@/components/Admin/OrgCharterView'
import { OrgUnitTree } from '@/components/Organisation/OrgUnitTree'
import { WebsiteAnalyticsSettings } from '@/components/Settings/WebsiteAnalyticsSettings'
import { ApiKeysSettings } from '@/components/Settings/ApiKeysSettings'

const MODULE_DEFS = [
  {
    key: 'social_media',
    label: 'Social Media',
    description: 'Publishing, scheduling, inbox, comments, engagement queue, content calendar.',
    icon: Share2,
    color: 'text-pink-500',
  },
  {
    key: 'coverage_analytics',
    label: 'Analytics',
    description: 'Post performance, account insights, audience demographics, competitor benchmarks.',
    icon: BarChart3,
    color: 'text-blue-500',
  },
  {
    key: 'press_releases',
    label: 'Press & Journalist',
    description: 'Press releases, media registry, accreditation, events, awards, media kits, journalist portal.',
    icon: Newspaper,
    color: 'text-amber-500',
  },
] as const;

export function OrgSettingsView() {
  const { orgId, can } = useOrgPermissions()
  const { toast } = useToast()
  const qc = useQueryClient()

  // ── Module flags ─────────────────────────────────────────────────────────
  const { data: moduleFlags = {}, refetch: refetchModules } = useQuery<Record<string, boolean>>({
    queryKey: ['org-modules', orgId],
    enabled: !!orgId,
    queryFn: async () => {
      const { data } = await (supabase as any).from('org_modules').select('module, enabled').eq('org_id', orgId);
      const result: Record<string, boolean> = {};
      for (const row of data ?? []) result[row.module] = row.enabled;
      return result;
    },
  });

  const toggleModule = async (moduleKey: string, enabled: boolean) => {
    if (!orgId) return;
    await (supabase as any).from('org_modules').upsert(
      { org_id: orgId, module: moduleKey, enabled, updated_at: new Date().toISOString() },
      { onConflict: 'org_id,module' }
    );
    refetchModules();
    qc.invalidateQueries({ queryKey: ['org-modules'] });
    toast({ title: enabled ? `${moduleKey} enabled` : `${moduleKey} disabled` });
  };

  // ── General tab ──────────────────────────────────────────────────────────
  const { data: org } = useQuery({
    queryKey: ['org-settings', orgId],
    enabled: !!orgId,
    queryFn: async () => {
      const { data } = await (supabase
        .from('organizations')
        .select('id, name, slug, comms_email, settings, brand_primary, brand_secondary, brand_accent, open_coverage, logo_url, accreditation_policy')
        .eq('id', orgId!)
        .single() as any)
      return data as any
    }
  })

  const [general, setGeneral] = useState({ name: '', slug: '', comms_email: '', open_coverage: false, core_hashtags: '', accreditation_policy: '' })
  const [savingGeneral, setSavingGeneral] = useState(false)

  useEffect(() => {
    if (org) {
      setGeneral({
        name: org.name ?? '',
        slug: org.slug ?? '',
        comms_email: org.comms_email ?? '',
        open_coverage: org.open_coverage ?? false,
        core_hashtags: org.settings?.core_hashtags ?? '',
        accreditation_policy: org.accreditation_policy ?? '',
      })
    }
  }, [org])

  const saveGeneral = async () => {
    if (!orgId) return
    setSavingGeneral(true)
    try {
      await (supabase
        .from('organizations')
        .update({
          name: general.name,
          slug: general.slug,
          comms_email: general.comms_email,
          open_coverage: general.open_coverage,
          accreditation_policy: general.accreditation_policy.trim() || null,
          settings: { ...(org?.settings ?? {}), core_hashtags: general.core_hashtags.trim() },
        })
        .eq('id', orgId) as any)
      toast({ title: 'Settings saved.' })
      qc.invalidateQueries({ queryKey: ['org-settings', orgId] })
    } catch {
      toast({ title: 'Failed to save.', variant: 'destructive' })
    } finally {
      setSavingGeneral(false)
    }
  }

  // ── Languages & Media Kits tab ───────────────────────────────────────────
  const { data: orgLangs } = useQuery({
    queryKey: ['org-languages', orgId],
    enabled: !!orgId,
    queryFn: async () => {
      const { data } = await (supabase
        .from('org_languages' as any)
        .select('*, org_media_kits(storage_path, uploaded_at)')
        .eq('org_id', orgId) as any)
      return (data ?? []) as any[]
    }
  })

  const uploadKit = async (langCode: string, file: File) => {
    if (!orgId) return
    const path = `${orgId}/${langCode}/media-kit.pdf`
    const { error: uploadError } = await supabase.storage
      .from('media-kits')
      .upload(path, file, { upsert: true })
    if (uploadError) {
      toast({ title: 'Upload failed.', variant: 'destructive' })
      return
    }
    await (supabase
      .from('org_media_kits' as any)
      .upsert({
        org_id: orgId,
        language_code: langCode,
        storage_path: path,
        uploaded_at: new Date().toISOString(),
      }) as any)
    await (supabase
      .from('org_languages' as any)
      .update({ is_active: true })
      .eq('org_id', orgId)
      .eq('language_code', langCode) as any)
    toast({ title: 'Media kit uploaded.' })
    qc.invalidateQueries({ queryKey: ['org-languages', orgId] })
  }

  const deactivateLang = async (langCode: string) => {
    if (!orgId) return
    await (supabase
      .from('org_languages' as any)
      .update({ is_active: false })
      .eq('org_id', orgId)
      .eq('language_code', langCode) as any)
    qc.invalidateQueries({ queryKey: ['org-languages', orgId] })
  }

  const downloadKit = async (path: string) => {
    const { data } = await supabase.storage.from('media-kits').createSignedUrl(path, 60)
    if (data?.signedUrl) window.open(data.signedUrl, '_blank')
  }

  // ── Branding tab ─────────────────────────────────────────────────────────
  const [branding, setBranding] = useState({ primary: '', secondary: '', accent: '' })
  const [savingBrand, setSavingBrand] = useState(false)
  const [uploadingLogo, setUploadingLogo] = useState(false)

  const uploadLogo = async (file: File) => {
    if (!orgId) return
    setUploadingLogo(true)
    try {
      const ext = file.name.split('.').pop() ?? 'png'
      const path = `${orgId}/logo.${ext}`
      const { error: uploadError } = await supabase.storage
        .from('org-logos')
        .upload(path, file, { upsert: true, contentType: file.type })
      if (uploadError) throw uploadError
      const { data: { publicUrl } } = supabase.storage.from('org-logos').getPublicUrl(path)
      await (supabase
        .from('organizations')
        .update({ logo_url: publicUrl })
        .eq('id', orgId) as any)
      toast({ title: 'Logo uploaded.' })
      qc.invalidateQueries({ queryKey: ['org-settings', orgId] })
      qc.invalidateQueries({ queryKey: ['portal-org-brand'] })
    } catch (err: any) {
      toast({ title: err.message ?? 'Logo upload failed.', variant: 'destructive' })
    } finally {
      setUploadingLogo(false)
    }
  }

  const removeLogo = async () => {
    if (!orgId) return
    await (supabase.from('organizations').update({ logo_url: null }).eq('id', orgId) as any)
    toast({ title: 'Logo removed.' })
    qc.invalidateQueries({ queryKey: ['org-settings', orgId] })
    qc.invalidateQueries({ queryKey: ['portal-org-brand'] })
  }

  useEffect(() => {
    if (org) {
      setBranding({
        primary:   org.brand_primary   ?? '#006B3C',
        secondary: org.brand_secondary ?? '#0D2240',
        accent:    org.brand_accent    ?? '#C8A84B',
      })
    }
  }, [org])

  const saveBranding = async () => {
    if (!orgId) return
    setSavingBrand(true)
    try {
      await (supabase
        .from('organizations')
        .update({
          brand_primary:   branding.primary,
          brand_secondary: branding.secondary,
          brand_accent:    branding.accent,
        })
        .eq('id', orgId) as any)
      // Re-apply CSS vars immediately
      const root = document.documentElement
      root.style.setProperty('--color-primary', branding.primary)
      root.style.setProperty('--color-accent',  branding.accent)
      root.style.setProperty('--color-header',  branding.primary)
      toast({ title: 'Brand colours saved.' })
      qc.invalidateQueries({ queryKey: ['org-settings', orgId] })
      qc.invalidateQueries({ queryKey: ['org-brand', orgId] })
    } catch {
      toast({ title: 'Failed to save branding.', variant: 'destructive' })
    } finally {
      setSavingBrand(false)
    }
  }

  const LANG_NAMES: Record<string, string> = { en: 'English', fr: 'French', pt: 'Portuguese' }

  // ── Accreditation restrictions ───────────────────────────────────────────
  const { data: accredRule, refetch: refetchAccredRule } = useQuery({
    queryKey: ['org-accred-rule', orgId],
    enabled: !!orgId,
    queryFn: async () => {
      const { data } = await (supabase
        .from('organizations' as any)
        .select('accreditation_restricted_to_association_id, require_membership_number')
        .eq('id', orgId!)
        .single() as any)
      return data as any
    },
  })

  const { data: allAssociations = [] } = useQuery({
    queryKey: ['all-associations-list'],
    queryFn: async () => {
      const { data } = await (supabase
        .from('associations' as any)
        .select('id, name, acronym')
        .order('name') as any)
      return (data ?? []) as any[]
    },
  })

  const [accredForm, setAccredForm] = useState({ association_id: 'none', require_number: false })
  const [savingAccred, setSavingAccred] = useState(false)

  useEffect(() => {
    if (accredRule) {
      setAccredForm({
        association_id: accredRule.accreditation_restricted_to_association_id ?? 'none',
        require_number: accredRule.require_membership_number ?? false,
      })
    }
  }, [accredRule])

  async function saveAccredRules() {
    if (!orgId) return
    setSavingAccred(true)
    try {
      await (supabase
        .from('organizations' as any)
        .update({
          accreditation_restricted_to_association_id: accredForm.association_id === 'none' ? null : accredForm.association_id,
          require_membership_number: accredForm.require_number,
        })
        .eq('id', orgId) as any)
      toast({ title: 'Accreditation rules saved.' })
      refetchAccredRule()
    } catch (e: any) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' })
    } finally {
      setSavingAccred(false)
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold">Organisation Settings</h2>
        <p className="text-justify text-muted-foreground text-sm mt-1">Manage your organisation's profile, branding, and media kits.</p>
      </div>

      <Tabs defaultValue="general">
        <TabsList>
          <TabsTrigger value="general">General</TabsTrigger>
          <TabsTrigger value="languages">Languages & Media Kits</TabsTrigger>
          <TabsTrigger value="branding">Branding</TabsTrigger>
          <TabsTrigger value="charter">Charter</TabsTrigger>
          <TabsTrigger value="accreditation">Accreditation Rules</TabsTrigger>
          <TabsTrigger value="structure">Org Structure</TabsTrigger>
          <TabsTrigger value="website-analytics">Website Analytics</TabsTrigger>
          <TabsTrigger value="modules"><ToggleLeft className="w-3.5 h-3.5 mr-1 inline" />Modules</TabsTrigger>
          <TabsTrigger value="developer"><KeyRound className="w-3.5 h-3.5 mr-1 inline" />Developer</TabsTrigger>
        </TabsList>

        {/* General */}
        <TabsContent value="general" className="mt-4">
          <Card className="max-w-xl">
            <CardHeader><CardTitle className="text-base">Organisation Details</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-1.5">
                <Label>Organisation Name</Label>
                <Input
                  value={general.name}
                  onChange={e => setGeneral(g => ({ ...g, name: e.target.value }))}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Slug</Label>
                <Input
                  value={general.slug}
                  onChange={e => setGeneral(g => ({ ...g, slug: e.target.value }))}
                  placeholder="ecowas-parliament"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Communications Email</Label>
                <Input
                  type="email"
                  value={general.comms_email}
                  onChange={e => setGeneral(g => ({ ...g, comms_email: e.target.value }))}
                  placeholder="comms@yourorg.org"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Core Hashtags</Label>
                <Input
                  value={general.core_hashtags}
                  onChange={e => setGeneral(g => ({ ...g, core_hashtags: e.target.value }))}
                  placeholder="#ECOWAS #WestAfrica"
                />
                <p className="text-justify text-[11px] text-muted-foreground">
                  Space-separated hashtags always included when generating social media tags for your posts.
                </p>
              </div>

              <div className="space-y-1.5">
                <Label>Accreditation Policy</Label>
                <Textarea
                  rows={6}
                  value={general.accreditation_policy}
                  onChange={e => setGeneral(g => ({ ...g, accreditation_policy: e.target.value }))}
                  placeholder="Describe your accreditation requirements, eligibility criteria, and review process. Shown to journalists before they apply."
                />
                <p className="text-justify text-[11px] text-muted-foreground">
                  Shown to journalists when they apply for accreditation to your institution, so they understand your requirements up front.
                </p>
              </div>

              {/* Open Coverage toggle */}
              <div className="flex items-start justify-between gap-4 pt-2">
                <div>
                  <div className="text-[13px] font-medium">Open Coverage</div>
                  <div className="text-[12px] text-muted-foreground mt-0.5 max-w-xs">
                    Allow any accredited journalist on CONORBIT to request coverage of your organisation.
                    When off, only journalists you invite are eligible.
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setGeneral(g => ({ ...g, open_coverage: !g.open_coverage }))}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none ${
                    general.open_coverage ? 'bg-[#006B3C]' : 'bg-muted'
                  }`}
                >
                  <span
                    className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition duration-200 ${
                      general.open_coverage ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>

              <Button onClick={saveGeneral} disabled={savingGeneral}>
                {savingGeneral ? 'Saving…' : 'Save Changes'}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Languages & Media Kits */}
        <TabsContent value="languages" className="mt-4">
          <div className="space-y-4 max-w-2xl">
            {(orgLangs ?? []).length === 0 && (
              <p className="text-justify text-sm text-muted-foreground">No languages configured for this organisation.</p>
            )}
            {(orgLangs ?? []).map((lang: any) => {
              const kit = lang.org_media_kits?.[0]
              return (
                <Card key={lang.language_code}>
                  <CardContent className="p-4 flex items-center gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm">
                          {LANG_NAMES[lang.language_code] ?? lang.language_code.toUpperCase()}
                        </span>
                        {lang.is_active ? (
                          <Badge className="bg-green-100 text-green-700 text-[10px] gap-1">
                            <CheckCircle className="w-3 h-3" /> Active
                          </Badge>
                        ) : (
                          <Badge className="bg-amber-100 text-amber-700 text-[10px] gap-1">
                            <AlertCircle className="w-3 h-3" /> No kit uploaded
                          </Badge>
                        )}
                      </div>
                      {kit && (
                        <p className="text-justify text-xs text-muted-foreground mt-0.5">
                          Last updated: {new Date(kit.uploaded_at).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {kit && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-8 text-xs gap-1"
                          onClick={() => downloadKit(kit.storage_path)}
                        >
                          <Download className="w-3.5 h-3.5" /> Download
                        </Button>
                      )}
                      <label className="cursor-pointer">
                        <input
                          type="file"
                          accept=".pdf"
                          className="hidden"
                          onChange={e => {
                            const file = e.target.files?.[0]
                            if (file) uploadKit(lang.language_code, file)
                          }}
                        />
                        <span className="inline-flex items-center gap-1 h-8 px-3 text-xs font-medium rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground transition-colors cursor-pointer">
                          <Upload className="w-3.5 h-3.5" /> {kit ? 'Replace' : 'Upload PDF'}
                        </span>
                      </label>
                      {lang.is_active && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 text-xs text-muted-foreground"
                          onClick={() => deactivateLang(lang.language_code)}
                        >
                          Deactivate
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )
            })}
          </div>
        </TabsContent>

        {/* Charter */}
        <TabsContent value="charter" className="mt-4">
          {orgId && (
            <OrgCharterView
              orgId={orgId}
              orgName={org?.name ?? ''}
              canEdit={can('organogram', 'full')}
            />
          )}
        </TabsContent>

        {/* Branding */}
        <TabsContent value="branding" className="mt-4">
          <div className="space-y-6">
            {/* Logo upload */}
            <Card className="max-w-sm">
              <CardHeader><CardTitle className="text-base">Organisation Logo</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                {org?.logo_url && (
                  <div className="flex items-center gap-4">
                    <img src={org.logo_url} alt="Logo" className="h-16 w-auto object-contain rounded border border-border p-1" />
                    <button onClick={removeLogo} className="text-xs text-destructive hover:underline">
                      Remove
                    </button>
                  </div>
                )}
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/webp,image/svg+xml"
                    className="hidden"
                    onChange={e => { const f = e.target.files?.[0]; if (f) uploadLogo(f) }}
                  />
                  <span className="inline-flex items-center gap-1.5 h-9 px-4 text-sm font-medium rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground transition-colors">
                    <Upload className="w-4 h-4" />
                    {uploadingLogo ? 'Uploading…' : org?.logo_url ? 'Replace Logo' : 'Upload Logo'}
                  </span>
                </label>
                <p className="text-justify text-[11px] text-muted-foreground">
                  PNG, JPG, SVG or WebP, max 2 MB. Shown on your branded portal login page and journalist portal header.
                  Your portal URL: <span className="font-mono">/orgs/{org?.slug ?? 'your-slug'}/login</span>
                </p>
              </CardContent>
            </Card>

            {/* Brand colours + live preview */}
            <div className="flex gap-8 flex-wrap">
              <Card className="flex-1 min-w-72 max-w-sm">
                <CardHeader><CardTitle className="text-base">Brand Colours</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  {[
                    { label: 'Primary Colour', key: 'primary', value: branding.primary },
                    { label: 'Secondary Colour', key: 'secondary', value: branding.secondary },
                    { label: 'Accent Colour', key: 'accent', value: branding.accent },
                  ].map(({ label, key, value }) => (
                    <div key={key} className="space-y-1.5">
                      <Label>{label}</Label>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={value}
                          onChange={e => setBranding(b => ({ ...b, [key]: e.target.value }))}
                          className="w-10 h-10 rounded cursor-pointer border-none p-0.5 bg-transparent"
                        />
                        <Input
                          value={value}
                          onChange={e => setBranding(b => ({ ...b, [key]: e.target.value }))}
                          className="font-mono text-sm"
                          placeholder="#000000"
                        />
                      </div>
                    </div>
                  ))}
                  <Button onClick={saveBranding} disabled={savingBrand} className="w-full">
                    {savingBrand ? 'Saving…' : 'Save & Apply'}
                  </Button>
                </CardContent>
              </Card>

              <div className="flex-1 min-w-60">
                <p className="text-justify text-sm font-medium mb-3 text-muted-foreground">Live Preview</p>
                <div className="w-48 rounded-xl overflow-hidden shadow-md border" style={{ backgroundColor: branding.primary }}>
                  <div className="p-3 border-b" style={{ borderColor: 'rgba(255,255,255,0.1)' }}>
                    <div className="text-xs font-bold tracking-widest uppercase" style={{ color: branding.accent }}>
                      ORG PORTAL
                    </div>
                  </div>
                  <div className="p-2 space-y-1">
                    {['Dashboard', 'Events', 'Media Registry'].map(item => (
                      <div key={item} className="px-2 py-1.5 rounded text-xs font-medium" style={{ color: 'rgba(255,255,255,0.65)' }}>
                        {item}
                      </div>
                    ))}
                  </div>
                  <div className="p-2 border-t" style={{ borderColor: 'rgba(255,255,255,0.1)' }}>
                    <div className="w-6 h-6 rounded-full flex items-center justify-center text-[9px] font-bold"
                      style={{ backgroundColor: branding.accent, color: branding.primary }}>
                      CC
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Accreditation Rules */}
        <TabsContent value="accreditation" className="mt-4">
          <Card className="max-w-xl">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-[#006B3C]" />
                Accreditation Rules
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              <p className="text-sm text-muted-foreground">
                Restrict which journalists this institution will accredit. When an association is
                selected, only journalists who are active members of that association can apply.
              </p>

              <div className="space-y-1.5">
                <Label>Required Association</Label>
                <Select
                  value={accredForm.association_id}
                  onValueChange={v => setAccredForm(f => ({ ...f, association_id: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="No restriction (open accreditation)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No restriction — open accreditation</SelectItem>
                    {allAssociations.map((a: any) => (
                      <SelectItem key={a.id} value={a.id}>
                        {a.name}{a.acronym ? ` (${a.acronym})` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {accredForm.association_id !== 'none' && (
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="require_number"
                    checked={accredForm.require_number}
                    onChange={e => setAccredForm(f => ({ ...f, require_number: e.target.checked }))}
                    className="w-4 h-4 rounded border-border accent-[#006B3C]"
                  />
                  <Label htmlFor="require_number" className="cursor-pointer font-normal">
                    Require a valid association membership number
                  </Label>
                </div>
              )}

              {accredForm.association_id !== 'none' && (
                <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-700">
                  Journalists without an active membership in the selected association will be
                  blocked from applying. Applications already in progress are not retroactively affected.
                </div>
              )}

              <Button onClick={saveAccredRules} disabled={savingAccred}>
                {savingAccred && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Save Rules
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
        {/* Org Structure */}
        <TabsContent value="structure" className="mt-4">
          <OrgUnitTree />
        </TabsContent>

        {/* Website Analytics */}
        <TabsContent value="website-analytics" className="mt-4">
          <Card>
            <CardContent className="pt-6">
              <WebsiteAnalyticsSettings />
            </CardContent>
          </Card>
        </TabsContent>

        {/* Module toggles */}
        <TabsContent value="modules" className="mt-4">
          <Card className="max-w-xl">
            <CardHeader>
              <CardTitle className="text-base">Product Modules</CardTitle>
              <p className="text-sm text-muted-foreground">
                Enable or disable entire product areas for your organisation. Disabled modules
                are hidden from all members' navigation regardless of their role permissions.
              </p>
            </CardHeader>
            <CardContent className="space-y-3">
              {MODULE_DEFS.map(mod => {
                const Icon = mod.icon;
                const enabled = moduleFlags[mod.key] !== false;
                return (
                  <div key={mod.key} className="flex items-start justify-between gap-4 p-4 rounded-xl border border-border">
                    <div className="flex items-start gap-3">
                      <div className={`mt-0.5 ${mod.color}`}>
                        <Icon className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold">{mod.label}</p>
                        <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{mod.description}</p>
                      </div>
                    </div>
                    <Switch
                      checked={enabled}
                      onCheckedChange={v => toggleModule(mod.key, v)}
                      disabled={!can('organogram', 'full')}
                    />
                  </div>
                );
              })}
              <p className="text-[11px] text-muted-foreground pt-1">
                Only org admins can change module settings. Changes take effect immediately for all members.
              </p>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="developer" className="mt-4">
          <ApiKeysSettings />
        </TabsContent>
      </Tabs>
    </div>
  )
}
